/**
 * Trousse d'icônes partagée. Un manifeste d'app déclare `icon: string` ; le cœur ne connaît que
 * ce nom et le cherche ici. **Aucune entrée de ce fichier ne nomme une app** : ce sont des formes
 * génériques (des gens, une enveloppe, un dossier), réutilisables par n'importe quelle app.
 *
 * Un nom inconnu ne casse rien et ne devine rien : `fallback` est rendu, une fenêtre carrée
 * neutre. C'est volontaire — une app livrée avec une faute de frappe dans son manifeste doit
 * rester lançable, pas disparaître du dock.
 *
 * Tous les tracés partagent la même grille (`viewBox 0 0 24 24`), le même trait (1.6, arrondi) et
 * `currentColor` : la couleur vient toujours du contexte, jamais du dessin.
 */
import type { JSX } from 'react';

interface PathShape {
  readonly path: string;
}
interface CircleShape {
  readonly circle: readonly [cx: number, cy: number, r: number];
}
export type IconShape = PathShape | CircleShape;

export const APP_ICONS: Readonly<Record<string, readonly IconShape[]>> = {
  'more-horizontal': [{ circle: [5, 12, 1] }, { circle: [12, 12, 1] }, { circle: [19, 12, 1] }],
  fallback: [
    { path: 'M6.5 4.5h11a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2h-11a2 2 0 0 1-2-2v-11a2 2 0 0 1 2-2z' },
    { circle: [12, 12, 2.2] },
  ],
  user: [{ circle: [12, 8, 3.4] }, { path: 'M5 19.5c.8-3.4 3.5-5.1 7-5.1s6.2 1.7 7 5.1' }],
  users: [
    { circle: [9, 8, 3] },
    { path: 'M3.5 19c.6-3 2.9-4.5 5.5-4.5s4.9 1.5 5.5 4.5' },
    { path: 'M16 5.3a3 3 0 0 1 0 5.9' },
    { path: 'M17.5 14.9c2 .6 3.4 2 3.9 4.1' },
  ],
  contacts: [
    { path: 'M4 5.5h16v13H4z' },
    { circle: [10, 11, 2] },
    { path: 'M6.5 16.5c.4-1.6 1.7-2.4 3.5-2.4s3.1.8 3.5 2.4' },
    { path: 'M15.5 10h3M15.5 13.5h3' },
  ],
  mail: [{ path: 'M3.5 6h17v12h-17z' }, { path: 'M3.5 7l8.5 6.5L20.5 7' }],
  phone: [{ path: 'M7.5 3.5h9v17h-9z' }, { path: 'M10.5 17.8h3' }],
  calendar: [{ path: 'M4 6h16v14H4z' }, { path: 'M4 10h16' }, { path: 'M8.5 3.5v4M15.5 3.5v4' }],
  clock: [{ circle: [12, 12, 8.3] }, { path: 'M12 7.2V12l3.2 2' }],
  chart: [
    { path: 'M4 20h16' },
    { path: 'M7.5 20v-6' },
    { path: 'M12 20V7' },
    { path: 'M16.5 20v-9' },
  ],
  file: [{ path: 'M6.5 3.5h7l4.5 4.5v12h-11.5z' }, { path: 'M13.5 3.5V8H18' }],
  folder: [{ path: 'M3.5 6.5h6l2 2.5h9v11h-17z' }],
  check: [{ path: 'M5 12.5l4.5 4.5L19 7.5' }],
  list: [
    { path: 'M8 7h11M8 12h11M8 17h11' },
    { circle: [4.6, 7, 1] },
    { circle: [4.6, 12, 1] },
    { circle: [4.6, 17, 1] },
  ],
  tag: [{ path: 'M3.5 11.2 11.2 3.5h9.3v9.3l-7.7 7.7z' }, { circle: [16.4, 7.6, 1.4] }],
  search: [{ circle: [10.8, 10.8, 6.3] }, { path: 'M15.4 15.4 20.5 20.5' }],
  settings: [
    { circle: [12, 12, 3.2] },
    { path: 'M12 3.2v2.4M12 18.4v2.4M20.8 12h-2.4M5.6 12H3.2' },
    { path: 'M18.2 5.8l-1.7 1.7M7.5 16.5l-1.7 1.7M18.2 18.2l-1.7-1.7M7.5 7.5 5.8 5.8' },
  ],
  lock: [{ path: 'M6.5 10.5h11v9.5h-11z' }, { path: 'M9 10.5V8a3 3 0 0 1 6 0v2.5' }],
  key: [{ circle: [8, 13, 4.2] }, { path: 'M11.4 10.6 20.5 1.5M17.7 4.3l2 2M15.5 6.5l2 2' }],
  home: [{ path: 'M4 11 12 4l8 7' }, { path: 'M6.5 9.6V20h11V9.6' }],
  book: [
    { path: 'M12 7.5a2.5 2.5 0 0 0-2.5-2.5H4v13h5.5A2.5 2.5 0 0 1 12 20.5' },
    { path: 'M12 7.5a2.5 2.5 0 0 1 2.5-2.5H20v13h-5.5a2.5 2.5 0 0 0-2.5 2.5' },
  ],
  clipboard: [
    { path: 'M8.5 4.5H7a2 2 0 0 0-2 2V19a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6.5a2 2 0 0 0-2-2h-1.5' },
    { path: 'M9 3h6v3H9z' },
  ],
  star: [{ path: 'M12 3.8l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 10l5.9-.9z' }],
  cart: [
    { path: 'M3.5 4.5h2.2l2.4 10.4h9.2l2.2-7.6H6.4' },
    { circle: [9.5, 19, 1.5] },
    { circle: [17, 19, 1.5] },
  ],
  euro: [{ path: 'M17.5 6.6a6.6 6.6 0 1 0 0 10.8' }, { path: 'M4.5 10.4h8.5M4.5 13.6h8.5' }],
  grid: [
    { path: 'M4.5 4.5h6v6h-6z' },
    { path: 'M13.5 4.5h6v6h-6z' },
    { path: 'M4.5 13.5h6v6h-6z' },
    { path: 'M13.5 13.5h6v6h-6z' },
  ],
  target: [{ circle: [12, 12, 8.2] }, { circle: [12, 12, 4.4] }, { circle: [12, 12, 1.2] }],
  megaphone: [{ path: 'M4.5 9.8v4.4h3l7 4.3V5.5l-7 4.3z' }, { path: 'M18 9.5a4 4 0 0 1 0 5' }],
  briefcase: [{ path: 'M3.5 8h17v11h-17z' }, { path: 'M9 8V5.5h6V8' }],
  building: [
    { path: 'M5.5 20.5V4.5h9v16' },
    { path: 'M14.5 10.5h4v10' },
    { path: 'M8 8h3.5M8 12h3.5M8 16h3.5' },
  ],
  sparkles: [
    { path: 'M12 4l1.6 4.4L18 10l-4.4 1.6L12 16l-1.6-4.4L6 10l4.4-1.6z' },
    { path: 'M18.5 15.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z' },
  ],
  message: [{ path: 'M4.5 5.5h15v10h-9l-4.5 4v-4h-1.5z' }],
  bell: [
    { path: 'M12 3.5a5.5 5.5 0 0 0-5.5 5.5c0 5-2 6.5-2 6.5h15s-2-1.5-2-6.5A5.5 5.5 0 0 0 12 3.5z' },
    { path: 'M10.2 19a2 2 0 0 0 3.6 0' },
  ],
  logout: [
    { path: 'M9.5 4.5H5.5v15h4' },
    { path: 'M13 8.5 16.5 12 13 15.5' },
    { path: 'M16.5 12H9' },
  ],
  plus: [{ path: 'M12 5.5v13M5.5 12h13' }],
  close: [{ path: 'M6 6l12 12M18 6L6 18' }],
  expand: [
    { path: 'M8.5 4.5h-4v4' },
    { path: 'M15.5 4.5h4v4' },
    { path: 'M8.5 19.5h-4v-4' },
    { path: 'M15.5 19.5h4v-4' },
  ],
  collapse: [
    { path: 'M4.5 8.5h4v-4' },
    { path: 'M19.5 8.5h-4v-4' },
    { path: 'M4.5 15.5h4v4' },
    { path: 'M19.5 15.5h-4v4' },
  ],
  /* Œil ouvert / œil barré : afficher ou masquer un mot de passe à la saisie. Même grille et même
     trait que le reste du fichier ; l'œil barré est l'œil ouvert plus une seule diagonale, pour
     qu'on lise d'un coup que c'est le même contrôle dans l'autre position. */
  eye: [
    { path: 'M2.5 12S6 6.2 12 6.2 21.5 12 21.5 12 18 17.8 12 17.8 2.5 12 2.5 12z' },
    { circle: [12, 12, 2.9] },
  ],
  'eye-off': [
    { path: 'M2.5 12S6 6.2 12 6.2 21.5 12 21.5 12 18 17.8 12 17.8 2.5 12 2.5 12z' },
    { circle: [12, 12, 2.9] },
    { path: 'M4 20 20 4' },
  ],
  'chevron-left': [{ path: 'M14.5 5.5 8 12l6.5 6.5' }],
  'chevron-right': [{ path: 'M9.5 5.5 16 12l-6.5 6.5' }],
  window: [{ path: 'M4 5h16v14H4zM4 9h16' }],
  'panel-left': [{ path: 'M4 5h16v14H4zM10 5v14M6 8v8M8 8v8' }],
  'panel-right': [{ path: 'M4 5h16v14H4zM14 5v14M16 8v8M18 8v8' }],
  'width-expand': [{ path: 'M3 12h18M7 8l-4 4 4 4M17 8l4 4-4 4' }],
  'width-collapse': [{ path: 'M3 12h6M15 12h6M5 8l4 4-4 4M19 8l-4 4 4 4' }],
  'height-expand': [{ path: 'M12 3v18M8 7l4-4 4 4M8 17l4 4 4-4' }],
  'height-collapse': [{ path: 'M12 3v6M12 15v6M8 5l4 4 4-4M8 19l4-4 4 4' }],
  'arrow-left': [{ path: 'M19.5 12h-14M11 6.5 5.5 12l5.5 5.5' }],
  'arrow-up': [{ path: 'M12 19.5v-14M6.5 11 12 5.5l5.5 5.5' }],
  'arrow-down': [{ path: 'M12 4.5v14M6.5 13l5.5 5.5 5.5-5.5' }],
  minus: [{ path: 'M5 12h14' }],
  'arrow-right': [{ path: 'M4.5 12h14M13 6.5l5.5 5.5-5.5 5.5' }],
};

export type IconName = keyof typeof APP_ICONS;

export interface IconProps {
  /** Nom de forme ; un nom inconnu rend `fallback`. */
  name: string;
  /** Côté du carré, en pixels. 23 dans le dock, 9 dans le badge de cadenas (référence). */
  size?: number;
  /** Épaisseur du trait. Reste proportionnelle quand l'icône rétrécit. */
  strokeWidth?: number;
  className?: string;
}

export function Icon({ name, size = 23, strokeWidth = 1.6, className }: IconProps): JSX.Element {
  const shapes = APP_ICONS[name] ?? APP_ICONS['fallback'] ?? [];
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {shapes.map((shape, index) =>
        'path' in shape ? (
          <path key={index} d={shape.path} />
        ) : (
          <circle key={index} cx={shape.circle[0]} cy={shape.circle[1]} r={shape.circle[2]} />
        ),
      )}
    </svg>
  );
}
